import * as React from 'react';

import { Box } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { Menu, MenuPopup, MenuTrigger } from '../../components/ui/menu';
import { DataTableAddColumnHeader } from './data-table-add-column';
import { DataTableCellContent } from './data-table-cell';
import type { DataTableColumnDef } from './data-table-types';
import { ACTIONS_COLUMN_WIDTH } from './data-table-utils';

/**
 * Actions cell component.
 * Wraps renderRowActions output in a Menu with a three-dot trigger.
 * Hidden until row hover on pointer devices; always visible below md.
 * (`data-popup-open` is Base UI's open-trigger attribute — it keeps the
 * button visible while its menu is open.)
 */
function ActionsCell<TData>({
  row,
  renderRowActions,
}: {
  row: TData;
  renderRowActions: (row: TData) => React.ReactNode;
}) {
  return (
    <DataTableCellContent align="end">
      <Box opacity={100} className="md:opacity-0 md:group-hover/row:opacity-100 md:focus-within:opacity-100 md:has-[[data-popup-open]]:opacity-100">
        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon-xs" aria-label="Open menu" />
            }
          >
            <Icon name="more-horizontal" />
          </MenuTrigger>
          <MenuPopup align="end">{renderRowActions(row)}</MenuPopup>
        </Menu>
      </Box>
    </DataTableCellContent>
  );
}

/**
 * Creates an actions column definition that is pinned to the right.
 * Content is hidden until row hover on pointer devices.
 *
 * The header is the "add column" picker (a "+" over the hidden-column
 * roster). It reads its data through `DataTableAddColumnContext` rather than
 * a parameter so this definition — and every per-group table built from it —
 * stays referentially stable while visibility changes; the picker renders
 * nothing when the table wires no `onColumnVisibilityChange`.
 *
 * @param renderRowActions - Render function receiving row data, returning MenuItem fragments
 */
export function createActionsColumn<TData>(
  renderRowActions: (row: TData) => React.ReactNode,
): DataTableColumnDef<TData, unknown> {
  return {
    id: '__actions__',
    header: () => (
      <DataTableCellContent align="end">
        <DataTableAddColumnHeader />
      </DataTableCellContent>
    ),
    cell: ({ row }) => (
      <ActionsCell row={row.original} renderRowActions={renderRowActions} />
    ),
    enableSorting: false,
    enableHiding: false,
    meta: {
      width: ACTIONS_COLUMN_WIDTH,
    },
  };
}
